import { useState, useEffect, useCallback } from 'react';
import LocalSearchSelect from '../components/LocalSearchSelect';
import { ArrowRightLeft, Truck, PackageCheck } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import api from '../api/client';

interface Branch { id: number; name: string; }
interface Location { id: number; name: string; branch_id: number; }
interface InventoryItem { product_id: number; product_name: string; sku: string; qty_available: number; cost_price: number; }

function statusOf(t: any): 'draft' | 'in_transit' | 'received' {
  if (t.received_at) return 'received';
  if (t.dispatched_at) return 'in_transit';
  return 'draft';
}

function StatusBadge({ status }: { status: string }) {
  const map: any = {
    draft: { bg: 'var(--sur2)', color: 'var(--tx3)', label: 'Draft' },
    in_transit: { bg: 'rgba(234,179,8,0.15)', color: '#eab308', label: 'In Transit' },
    received: { bg: 'rgba(34,197,94,0.15)', color: 'var(--green)', label: 'Received' },
  };
  const s = map[status] || map.draft;
  return <span style={{ background: s.bg, color: s.color, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{s.label}</span>;
}

export default function StockTransfer() {
  const { showToast } = useToast();
  const [branches, setBranches]   = useState<Branch[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [coa, setCoa]             = useState<any[]>([]);
  const [transfers, setTransfers] = useState<any[]>([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [showForm, setShowForm]   = useState(false);
  const [fromStock, setFromStock] = useState<InventoryItem[]>([]);

  const emptyForm = { from_branch_id:'', to_branch_id:'', from_location_id:'', to_location_id:'', transfer_date: new Date().toISOString().split('T')[0], notes:'' };
  const [form, setForm]           = useState<any>(emptyForm);
  const [lines, setLines]         = useState<any[]>([{ product_id:'', qty:'' }]);

  // Receive modal state
  const [receiveTarget, setReceiveTarget] = useState<any>(null);
  const [editId, setEditId] = useState<number|null>(null);
  const [editWasReceived, setEditWasReceived] = useState(false);
  const [receiveDate, setReceiveDate]     = useState(new Date().toISOString().split('T')[0]);
  const [receiveLanded, setReceiveLanded] = useState<any[]>([]);
  const [receiving, setReceiving]         = useState(false);
  const [receiveItems, setReceiveItems]   = useState<any[]>([]);
  const [rlSplits, setRlSplits]           = useState<any[]>([{ account_id:'', amount:'' }]);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/stock-transfers').then(r => { setTransfers(r.data.data); setLoading(false); });
  }, []);

  useEffect(() => {
    api.get('/branches').then(r => setBranches(r.data.data));
    api.get('/locations').then(r => setLocations(r.data.data));
    api.get('/coa').then(r => setCoa(r.data.data));
    load();
  }, [load]);

  useEffect(() => {
    if (form.from_location_id) {
      api.get('/inventory', { params: { location_id: form.from_location_id, limit: 200 } })
        .then(r => setFromStock(r.data.data || []));
    } else { setFromStock([]); }
  }, [form.from_location_id]);

  const fromLocations = locations.filter(l => !form.from_branch_id || String(l.branch_id) === String(form.from_branch_id));
  const toLocations   = locations.filter(l => !form.to_branch_id   || String(l.branch_id) === String(form.to_branch_id));

  const addLine    = () => setLines(l => [...l, { product_id:'', qty:'' }]);
  const removeLine = (i: number) => setLines(l => l.filter((_,idx) => idx !== i));
  const updateLine = (i: number, f: string, v: string) => setLines(l => l.map((line,idx) => idx===i ? {...line,[f]:v} : line));

  const handleSubmit = async () => {
    if (!form.from_branch_id || !form.to_branch_id || !form.from_location_id || !form.to_location_id) { showToast('Please fill all branch and location fields', 'error'); return; }
    const items = lines.filter(l => l.product_id && l.qty).map(l => ({ product_id: Number(l.product_id), qty: Number(l.qty) }));
    if (!items.length) { showToast('Add at least one product', 'error'); return; }
    setSaving(true);
    try {
      const res = editId
        ? await api.put('/stock-transfers/' + editId, { ...form, from_branch_id: Number(form.from_branch_id), to_branch_id: Number(form.to_branch_id), from_location_id: Number(form.from_location_id), to_location_id: Number(form.to_location_id),
            items: lines.filter((l:any) => l.product_id).map((l:any) => ({ product_id: Number(l.product_id), qty: Number(l.qty), landed_per_unit: Number(l.landed_per_unit || 0) })),
            landed_costs: receiveLanded.filter((l:any) => l.amount && l.credit_account_id).map((l:any) => ({ cost_type:l.cost_type, amount:Number(l.amount), credit_account_id:Number(l.credit_account_id), allocation_method:l.allocation_method, description:l.description })),
            line_landed_splits: rlSplits.filter((s:any) => s.account_id).map((s:any) => ({ account_id: Number(s.account_id), amount: rlSplits.filter((x:any) => x.account_id).length === 1 ? null : Number(s.amount) })) })
        : await api.post('/stock-transfers', { ...form, from_branch_id: Number(form.from_branch_id), to_branch_id: Number(form.to_branch_id), from_location_id: Number(form.from_location_id), to_location_id: Number(form.to_location_id), items });
      showToast(res.data.message);
      setShowForm(false); setEditId(null); setEditWasReceived(false); setForm(emptyForm); setLines([{ product_id:'', qty:'' }]); load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const grandTotal = lines.reduce((s, l) => {
    const stock = fromStock.find(i => String(i.product_id) === String(l.product_id));
    return s + (Number(l.qty) * Number(stock?.cost_price || 0));
  }, 0);

  // --- Receive modal handlers ---
  const openCorrect = async (t: any) => {
    try {
      const r = await api.get('/stock-transfers/' + t.id);
      const d = r.data.data;
      setForm({ from_branch_id: String(t.from_branch_id || d.from_branch_id), to_branch_id: String(t.to_branch_id || d.to_branch_id),
        from_location_id: String(d.from_location_id), to_location_id: String(d.to_location_id),
        transfer_date: (d.transfer_date || '').substring(0,10), notes: d.notes || '' });
      setLines((d.items || []).map((i:any) => ({ product_id: String(i.product_id), product_name: i.product_name, qty: String(Number(i.qty)) })));
      setEditId(t.id);
      setEditWasReceived(!!(t.received_at || d.received_at));
      setLines((d.items || []).map((i:any) => ({ product_id: String(i.product_id), product_name: i.product_name, qty: String(Number(i.qty)), landed_per_unit: Number(i.landed_per_unit) ? String(Number(i.landed_per_unit)) : '' })));
      setReceiveLanded((d.landed_costs || []).map((l:any) => ({ cost_type:l.cost_type, amount:String(l.amount), credit_account_id:String(l.credit_account_id), allocation_method:l.allocation_method || 'by_value', description:l.description || '' })));
      try {
        const s = d.line_landed_splits ? JSON.parse(d.line_landed_splits) : [];
        setRlSplits(s.length ? s.map((x:any) => ({ account_id:String(x.account_id), amount: x.amount == null ? '' : String(x.amount) })) : [{ account_id:'', amount:'' }]);
      } catch { setRlSplits([{ account_id:'', amount:'' }]); }
      setShowForm(true);
    } catch (e:any) { showToast(e.response?.data?.message || 'Could not open the transfer', 'error'); }
  };

  const doVoid = async (t: any) => {
    const reason = window.prompt('Why is ' + t.transfer_no + ' being voided?');
    if (reason === null) return;
    try {
      const r = await api.post('/stock-transfers/' + t.id + '/void', { reason: reason || null });
      showToast(r.data.message); load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Void failed', 'error'); }
  };

  const openReceive = async (t: any) => {
    setReceiveTarget(t);
    setReceiveDate(new Date().toISOString().split('T')[0]);
    setReceiveLanded([]);
    setRlSplits([{ account_id:'', amount:'' }]);
    setReceiveItems([]);
    try {
      const r = await api.get('/stock-transfers/' + t.id);
      setReceiveItems((r.data.data.items || []).map((i:any) => ({ ...i, landed_per_unit: '' })));
    } catch { }
  };
  const closeReceive = () => setReceiveTarget(null);
  const addReceiveLanded    = () => setReceiveLanded(l => [...l, { cost_type:'Transport', amount:'', credit_account_id:'', allocation_method:'by_value', description:'' }]);
  const removeReceiveLanded = (i: number) => setReceiveLanded(l => l.filter((_,idx) => idx !== i));
  const updateReceiveLanded = (i: number, f: string, v: string) => setReceiveLanded(l => l.map((lc,idx) => idx===i ? {...lc,[f]:v} : lc));

  const submitReceive = async () => {
    if (!receiveTarget) return;
    const lc = receiveLanded.filter(l => l.amount && l.credit_account_id).map(l => ({ cost_type: l.cost_type, amount: Number(l.amount), credit_account_id: Number(l.credit_account_id), allocation_method: l.allocation_method, description: l.description }));
    setReceiving(true);
    try {
      const perUnitTotal = receiveItems.reduce((s:number,i:any) => s + Number(i.qty||0) * Number(i.landed_per_unit||0), 0);
      if (perUnitTotal > 0) {
        const chosen = rlSplits.filter((s:any) => s.account_id);
        if (!chosen.length) { showToast('Choose the account the per-unit landed costs are paid from', 'error'); setReceiving(false); return; }
        if (chosen.length > 1 && Math.abs(chosen.reduce((t:number,s:any) => t + Number(s.amount||0), 0) - perUnitTotal) > 0.005) {
          showToast('The split amounts must add up to ' + perUnitTotal.toFixed(2), 'error'); setReceiving(false); return;
        }
      }
      const res = await api.post('/stock-transfers/' + receiveTarget.id + '/receive', {
        received_date: receiveDate, landed_costs: lc,
        items: receiveItems.map((i:any) => ({ id: i.id, landed_per_unit: Number(i.landed_per_unit || 0) })),
        line_landed_splits: rlSplits.filter((s:any) => s.account_id).map((s:any) => ({ account_id: Number(s.account_id), amount: rlSplits.filter((x:any) => x.account_id).length === 1 ? null : Number(s.amount) })),
      });
      showToast(res.data.message);
      closeReceive();
      load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed to receive', 'error'); }
    finally { setReceiving(false); }
  };

  return (
    <div>
      <div className='page-header'>
        <h1 className='page-title'>Stock Transfer</h1>
        <button className='btn btn-primary' type='button' onClick={() => setShowForm(!showForm)}><ArrowRightLeft size={13} />{editId ? (editWasReceived ? 'Correct Transfer (Received)' : 'Correct Transfer (In Transit)') : 'New Transfer'}</button>
      </div>
      {showForm && (
        <div className='card' style={{ marginBottom:16 }}>
          <div className='card-header'><span>{editId ? (editWasReceived ? 'Correct Transfer — stays received' : 'Correct Transfer — stays in transit') : 'New Stock Transfer (Dispatch)'}</span><button className='btn btn-secondary btn-sm' type='button' onClick={() => setShowForm(false)}>Cancel</button></div>
          <div className='card-body'>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:16 }}>
              <div className='form-group'>
                <label className='form-label'>From Branch *</label>
                <select className='form-control' value={form.from_branch_id} onChange={e => setForm((f:any) => ({...f, from_branch_id:e.target.value, from_location_id:''}))}>
                  <option value=''>Select branch</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className='form-group'>
                <label className='form-label'>From Location *</label>
                <select className='form-control' value={form.from_location_id} onChange={e => setForm((f:any) => ({...f, from_location_id:e.target.value}))}>
                  <option value=''>Select location</option>
                  {fromLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div className='form-group'>
                <label className='form-label'>Transfer Date *</label>
                <input type='date' className='form-control' value={form.transfer_date} onChange={e => setForm((f:any) => ({...f, transfer_date:e.target.value}))} />
              </div>
              <div className='form-group'>
                <label className='form-label'>To Branch *</label>
                <select className='form-control' value={form.to_branch_id} onChange={e => setForm((f:any) => ({...f, to_branch_id:e.target.value, to_location_id:''}))}>
                  <option value=''>Select branch</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className='form-group'>
                <label className='form-label'>To Location *</label>
                <select className='form-control' value={form.to_location_id} onChange={e => setForm((f:any) => ({...f, to_location_id:e.target.value}))}>
                  <option value=''>Select location</option>
                  {toLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div className='form-group'>
                <label className='form-label'>Notes</label>
                <input className='form-control' value={form.notes} onChange={e => setForm((f:any) => ({...f, notes:e.target.value}))} />
              </div>
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:8 }}>
              <thead><tr style={{ background:'var(--sur2)' }}>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Product</th>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Available</th>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Unit Cost</th>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Qty</th>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Total</th>
                <th></th>
              </tr></thead>
              <tbody>
                {lines.map((line, i) => {
                  const stock = fromStock.find(s => String(s.product_id) === String(line.product_id));
                  return (
                    <tr key={i} style={{ borderBottom:'1px solid var(--bdr)' }}>
                      <td style={{ padding:'6px 4px' }}>
                        <LocalSearchSelect
                          options={fromStock.map((s: any) => ({ value: s.product_id, label: s.product_name, sublabel: s.sku }))}
                          value={line.product_id}
                          fontSize={12}
                          placeholder='Search product...'
                          onSelect={(v) => updateLine(i, 'product_id', String(v))}
                        />
                      </td>
                      <td style={{ padding:'6px 4px', fontSize:12, color:'var(--green)' }}>{stock ? Number(stock.qty_available).toLocaleString() : '-'}</td>
                      <td style={{ padding:'6px 4px', fontSize:12 }}>{stock ? Number(stock.cost_price).toFixed(2) : '-'}</td>
                      <td style={{ padding:'6px 4px' }}><input type='number' className='form-control' value={line.qty} onChange={e => updateLine(i,'qty',e.target.value)} style={{ fontSize:12, textAlign:'right', width:80 }} /></td>
                      <td style={{ padding:'6px 4px', fontSize:12, textAlign:'right' }}>{stock && line.qty ? (Number(line.qty) * Number(stock.cost_price)).toFixed(2) : '0.00'}</td>
                      <td style={{ padding:'6px 4px' }}><button className='btn btn-secondary btn-sm' type='button' onClick={() => removeLine(i)}>x</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <button className='btn btn-secondary btn-sm' type='button' onClick={addLine} style={{ marginBottom:16 }}>+ Add Product</button>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              {editId && editWasReceived && (
                <div style={{margin:'14px 0',borderTop:'1px solid var(--bdr)',paddingTop:12}}>
                  <div style={{fontSize:12,fontWeight:600,color:'var(--tx2)',marginBottom:8}}>Landed / unit</div>
                  <table style={{width:'100%',borderCollapse:'collapse',marginBottom:10}}>
                    <tbody>
                      {lines.map((l,i) => l.product_id ? (
                        <tr key={i} style={{borderBottom:'1px solid var(--bdr)'}}>
                          <td style={{padding:'6px 8px',fontSize:12}}>{l.product_name}</td>
                          <td style={{padding:4,width:120}}><input className='form-control mono' type='number' style={{textAlign:'right'}} placeholder='0' value={l.landed_per_unit || ''} onChange={e => setLines(x => x.map((y,j) => j===i ? {...y, landed_per_unit:e.target.value} : y))} /></td>
                        </tr>
                      ) : null)}
                    </tbody>
                  </table>
                  {(() => {
                    const t = lines.reduce((s,x) => s + Number(x.qty||0) * Number(x.landed_per_unit||0), 0);
                    if (t <= 0) return null;
                    const sp = rlSplits.reduce((s,x) => s + Number(x.amount||0), 0);
                    return (
                      <div style={{marginBottom:12,fontSize:12}}>
                        <div style={{marginBottom:6}}>Per-unit landed costs: <strong style={{fontFamily:'monospace'}}>{t.toFixed(2)}</strong> &mdash; paid from:</div>
                        {rlSplits.map((s,i) => (
                          <div key={i} style={{display:'flex',gap:6,marginBottom:4,maxWidth:560}}>
                            <select className='form-control' style={{flex:1}} value={s.account_id} onChange={e => setRlSplits(x => x.map((y,j) => j===i ? {...y, account_id:e.target.value} : y))}><option value=''>Select account</option>{coa.map((a) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}</select>
                            {rlSplits.length > 1 && <input className='form-control mono' style={{width:110,textAlign:'right'}} placeholder='Amount' value={s.amount} onChange={e => setRlSplits(x => x.map((y,j) => j===i ? {...y, amount:e.target.value} : y))} />}
                            {rlSplits.length > 1 && <button type='button' className='btn btn-secondary btn-sm' onClick={() => setRlSplits(x => x.filter((_,j) => j!==i))}>x</button>}
                          </div>
                        ))}
                        <button type='button' className='btn btn-secondary btn-sm' onClick={() => setRlSplits(x => [...x, { account_id:'', amount:'' }])}>+ Split account</button>
                        {rlSplits.length > 1 && <span style={{marginLeft:10,color: Math.abs(sp - t) > 0.005 ? 'var(--red)' : 'var(--tx3)'}}>Splits {sp.toFixed(2)} of {t.toFixed(2)}</span>}
                      </div>
                    );
                  })()}
                  <div style={{fontSize:12,fontWeight:600,color:'var(--tx2)',marginBottom:8}}>Landed Costs</div>
                  {receiveLanded.length > 0 && (
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead><tr style={{background:'var(--sur2)'}}>
                        <th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Cost type</th><th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Amount</th><th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Paid from</th><th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Allocation</th><th></th>
                      </tr></thead>
                      <tbody>
                        {receiveLanded.map((lc,i) => (
                          <tr key={i}>
                            <td style={{padding:4,width:130}}><select className='form-control' value={lc.cost_type} onChange={e => updateReceiveLanded(i,'cost_type',e.target.value)}><option>Transport</option><option>Customs</option><option>Handling</option><option>Insurance</option><option>Other</option></select></td>
                            <td style={{padding:4,width:110}}><input type='number' className='form-control mono' style={{textAlign:'right'}} value={lc.amount} onChange={e => updateReceiveLanded(i,'amount',e.target.value)} /></td>
                            <td style={{padding:4}}><select className='form-control' value={lc.credit_account_id} onChange={e => updateReceiveLanded(i,'credit_account_id',e.target.value)}><option value=''>Select account</option>{coa.map((a) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}</select></td>
                            <td style={{padding:4,width:130}}><select className='form-control' value={lc.allocation_method} onChange={e => updateReceiveLanded(i,'allocation_method',e.target.value)}><option value='by_value'>By value</option><option value='by_qty'>By quantity</option></select></td>
                            <td style={{padding:4,width:36}}><button className='btn btn-secondary btn-sm' type='button' onClick={() => removeReceiveLanded(i)}>x</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <button className='btn btn-secondary btn-sm' type='button' style={{marginTop:8}} onClick={addReceiveLanded}>+ Add cost</button>
                </div>
              )}
              <span style={{ fontSize:14, fontWeight:700, color:'var(--red)' }}>Transfer Total: {grandTotal.toFixed(2)}</span>
              <button className='btn btn-primary' type='button' onClick={handleSubmit} disabled={saving}>{saving ? 'Dispatching...' : (editId ? 'Save correction' : 'Dispatch Transfer')}</button>
            </div>
          </div>
        </div>
      )}

      {receiveTarget && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div className='card' style={{ width:760, maxHeight:'85vh', overflowY:'auto' }}>
            <div className='card-header'><span>Receive Transfer {receiveTarget.transfer_no}</span><button className='btn btn-secondary btn-sm' type='button' onClick={closeReceive}>Cancel</button></div>
            <div className='card-body'>
              <div className='form-group'>
                <label className='form-label'>Received Date *</label>
                <input type='date' className='form-control' value={receiveDate} onChange={e => setReceiveDate(e.target.value)} />
              </div>
              <div style={{ margin:'16px 0' }}>
                <div style={{ fontSize:12, fontWeight:600, color:'var(--tx2)', marginBottom:8 }}>Items Dispatched</div>
                <table style={{width:'100%',borderCollapse:'collapse',marginBottom:10}}>
                  <thead><tr style={{background:'var(--sur2)'}}>
                    <th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Product</th><th style={{...{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}, textAlign:'right'}}>Qty</th><th style={{...{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}, textAlign:'right'}}>Cost</th><th style={{...{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}, textAlign:'right'}}>Landed / unit</th>
                  </tr></thead>
                  <tbody>
                    {receiveItems.map((it:any, i:number) => (
                      <tr key={it.id} style={{borderBottom:'1px solid var(--bdr)'}}>
                        <td style={{padding:'6px 8px',fontSize:12}}>{it.product_name}</td>
                        <td style={{padding:'6px 8px',fontSize:12,textAlign:'right',fontFamily:'monospace'}}>{Number(it.qty)}</td>
                        <td style={{padding:'6px 8px',fontSize:12,textAlign:'right',fontFamily:'monospace'}}>{Number(it.unit_cost).toFixed(2)}</td>
                        <td style={{padding:4,width:110}}><input className='form-control mono' type='number' style={{textAlign:'right'}} placeholder='0' value={it.landed_per_unit} onChange={e => setReceiveItems(x => x.map((y:any,j:number) => j===i ? {...y, landed_per_unit:e.target.value} : y))} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(() => {
                  const t = receiveItems.reduce((s:number,x:any) => s + Number(x.qty||0) * Number(x.landed_per_unit||0), 0);
                  if (t <= 0) return null;
                  const sp = rlSplits.reduce((s:number,x:any) => s + Number(x.amount||0), 0);
                  return (
                    <div style={{marginBottom:12,fontSize:12}}>
                      <div style={{marginBottom:6}}>Per-unit landed costs: <strong style={{fontFamily:'monospace'}}>{t.toFixed(2)}</strong> — paid from:</div>
                      {rlSplits.map((s:any, i:number) => (
                        <div key={i} style={{display:'flex',gap:6,marginBottom:4}}>
                          <select className='form-control' style={{flex:1}} value={s.account_id} onChange={e => setRlSplits(x => x.map((y:any,j:number) => j===i ? {...y, account_id:e.target.value} : y))}><option value=''>Select account</option>{coa.map((a:any) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}</select>
                          {rlSplits.length > 1 && <input className='form-control mono' style={{width:110,textAlign:'right'}} placeholder='Amount' value={s.amount} onChange={e => setRlSplits(x => x.map((y:any,j:number) => j===i ? {...y, amount:e.target.value} : y))} />}
                          {rlSplits.length > 1 && <button type='button' className='btn btn-secondary btn-sm' onClick={() => setRlSplits(x => x.filter((_:any,j:number) => j!==i))}>x</button>}
                        </div>
                      ))}
                      <button type='button' className='btn btn-secondary btn-sm' onClick={() => setRlSplits(x => [...x, { account_id:'', amount:'' }])}>+ Split account</button>
                      {rlSplits.length > 1 && <span style={{marginLeft:10,color: Math.abs(sp - t) > 0.005 ? 'var(--red)' : 'var(--tx3)'}}>Splits {sp.toFixed(2)} of {t.toFixed(2)}</span>}
                    </div>
                  );
                })()}
                <div style={{ fontSize:12, fontWeight:600, color:'var(--tx2)', marginBottom:8 }}>Landed Costs (Optional)</div>
                {receiveLanded.length > 0 && (
                  <table style={{width:'100%',borderCollapse:'collapse'}}>
                    <thead><tr style={{background:'var(--sur2)'}}>
                      <th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Cost type</th><th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Amount</th><th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Paid from</th><th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Allocation</th><th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Description</th><th></th>
                    </tr></thead>
                    <tbody>
                      {receiveLanded.map((lc:any, i:number) => (
                        <tr key={i}>
                          <td style={{padding:4,width:130}}><select className='form-control' value={lc.cost_type} onChange={e => updateReceiveLanded(i,'cost_type',e.target.value)}><option>Transport</option><option>Customs</option><option>Handling</option><option>Insurance</option><option>Other</option></select></td>
                          <td style={{padding:4,width:110}}><input type='number' className='form-control mono' style={{textAlign:'right'}} placeholder='0.00' value={lc.amount} onChange={e => updateReceiveLanded(i,'amount',e.target.value)} /></td>
                          <td style={{padding:4}}><select className='form-control' value={lc.credit_account_id} onChange={e => updateReceiveLanded(i,'credit_account_id',e.target.value)}><option value=''>Select account</option>{coa.map((a:any) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}</select></td>
                          <td style={{padding:4,width:130}}><select className='form-control' value={lc.allocation_method} onChange={e => updateReceiveLanded(i,'allocation_method',e.target.value)}><option value='by_value'>By value</option><option value='by_qty'>By quantity</option></select></td>
                          <td style={{padding:4}}><input className='form-control' value={lc.description || ''} onChange={e => updateReceiveLanded(i,'description',e.target.value)} /></td>
                          <td style={{padding:4,width:36}}><button className='btn btn-secondary btn-sm' type='button' onClick={() => removeReceiveLanded(i)}>x</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:8}}>
                  <button className='btn btn-secondary btn-sm' type='button' onClick={addReceiveLanded}>+ Add cost</button>
                  {receiveLanded.length > 0 && <span style={{fontSize:12,color:'var(--tx3)'}}>Total <strong style={{fontFamily:'monospace'}}>{receiveLanded.reduce((t:number,x:any) => t + Number(x.amount||0), 0).toFixed(2)}</strong></span>}
                </div>
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end' }}>
                <button className='btn btn-primary' type='button' onClick={submitReceive} disabled={receiving}>{receiving ? 'Receiving...' : 'Confirm Receive'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='card'>
        <div className='card-body' style={{ padding:0 }}>
          {loading ? <div style={{ padding:20, textAlign:'center' }}>Loading...</div> : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'var(--sur2)' }}>
                <th style={{ padding:'10px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'left' }}>Transfer No</th>
                <th style={{ padding:'10px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'left' }}>From</th>
                <th style={{ padding:'10px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'left' }}>To</th>
                <th style={{ padding:'10px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'left' }}>Date</th>
                <th style={{ padding:'10px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'left' }}>Status</th>
                <th style={{ padding:'10px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Landed Cost</th>
                <th></th>
              </tr></thead>
              <tbody>
                {transfers.map((t:any) => {
                  const status = statusOf(t);
                  return (
                    <tr key={t.id} style={{ borderBottom:'1px solid var(--bdr)' }}>
                      <td style={{ padding:'10px 12px', fontSize:12, fontWeight:600, color:'var(--blue)' }}>{t.transfer_no}</td>
                      <td style={{ padding:'10px 12px', fontSize:12 }}>{t.from_branch} - {t.from_location}</td>
                      <td style={{ padding:'10px 12px', fontSize:12 }}>{t.to_branch} - {t.to_location}</td>
                      <td style={{ padding:'10px 12px', fontSize:12 }}>{t.transfer_date}</td>
                      <td style={{ padding:'10px 12px' }}><StatusBadge status={status} /></td>
                      <td style={{ padding:'10px 12px', fontSize:12, textAlign:'right' }}>{Number(t.landed_cost_total || 0).toFixed(2)}</td>
                      <td style={{ padding:'10px 12px', textAlign:'right' }}>
                        {status === 'in_transit' && (
                          <button className='btn btn-primary btn-sm' type='button' onClick={() => openReceive(t)}>
                            <PackageCheck size={12} /> Receive
                          </button>
                        )}
                        {status !== 'draft' && <button className='btn btn-secondary btn-sm' type='button' style={{marginLeft:6}} onClick={() => openCorrect(t)}>Correct</button>}
                        {status !== 'draft' && <button className='btn btn-secondary btn-sm' type='button' style={{marginLeft:6}} onClick={() => doVoid(t)}>Void</button>}
                        {status === 'draft' && <Truck size={14} color='var(--tx3)' />}
                      </td>
                    </tr>
                  );
                })}
                {!transfers.length && <tr><td colSpan={7} style={{ padding:20, textAlign:'center', color:'var(--tx3)' }}>No transfers yet</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
